import {DateTime} from 'luxon';
import apiV2Service from '../api/api-v2.service';
import {ApiOptions} from '../api/apiTypes';
import {backgroundProcessService} from '../backgroundProcess/backgroundProcess.service';
import {RunningBGProcess} from '../backgroundProcess/backgroundProcess.types';
import {JobExecutionParams} from '../jobs/jobs.types';
import {AssetType} from './Assets.enums';
import {
  Asset,
  AssetFolderEntityType,
  CopyPasteAssetsRequest,
  Folder,
  FolderAndAssetRequestItem,
  GenerateZipRequest,
  LinkAssetRequest,
  MoveAssetsRequest,
  NewAssetCreationgRequestForFolder,
  NewFolderRequest,
  RemoteAsset,
  RemoteFolder,
  RemoteTechnicalMetadata,
  TechnicalMetadata,
} from './Assets.types';

const BASE_PATH = 'Assets';

/**
 * Get root folder for a given entity type
 * @param entityType - Type of entity (AssetsLibrary, Creative, CreativeTemplate, Brand)
 * @param depth - Optional depth of the tree to retrieve
 * @param options - API options
 * @returns Promise<Folder> - Tree structure with folders and assets
 */
async function getRootFolder(
  entityType: AssetFolderEntityType,
  depth?: number,
  options?: ApiOptions,
): Promise<Folder> {
  const {data} = await apiV2Service.executeGetRequest<RemoteFolder>(
    `${BASE_PATH}/${entityType}/folders/by-root`,
    {
      ...(depth !== undefined && {depth}),
    },
    options,
  );

  return convertFolderToClient(data);
}

/**
 * Get root folder for a specific entity
 * @param entityType - Type of entity (AssetsLibrary, Creative, CreativeTemplate, Brand)
 * @param entityId - ID of the entity
 * @param depth - Optional depth of the tree to retrieve
 * @param options - API options
 * @returns Promise<Folder> - Tree structure with folders and assets
 */
async function getRootFolderOfEntityObject(
  entityType: AssetFolderEntityType,
  entityId: number,
  depth?: number,
  options?: ApiOptions,
): Promise<Folder> {
  const {data} = await apiV2Service.executeGetRequest<RemoteFolder>(
    `${BASE_PATH}/${entityType}/folders/by-entity-id/${entityId}`,
    {
      ...(depth !== undefined && {depth}),
    },
    options,
  );

  return convertFolderToClient(data);
}

/**
 * Get folder tree branch starting from a specific folder
 * @param entityType - Type of entity (AssetsLibrary, Creative, CreativeTemplate, Brand)
 * @param folderId - ID of the folder to start from
 * @param depth - Optional depth of the tree to retrieve
 * @param nameSearchTerm - Optional search term to filter folders/assets by name
 * @param options - API options
 * @returns Promise<Folder> - Tree structure with folders and assets starting from the specified folder
 */
async function GetFolder(
  entityType: AssetFolderEntityType,
  folderId: number,
  depth?: number,
  nameSearchTerm?: string,
  options?: ApiOptions,
): Promise<Folder> {
  const {data} = await apiV2Service.executeGetRequest<RemoteFolder>(
    `${BASE_PATH}/${entityType}/folders/by-folder-id/${folderId}`,
    {
      ...(depth !== undefined && {depth}),
      ...(nameSearchTerm && {nameSearchTerm}),
    },
    options,
  );

  const folder = convertFolderToClient(data);

  return folder;
}

/**
 * Get asset by ID
 * @param assetId - ID of the asset to retrieve
 * @param options - API options
 * @returns Promise<Asset> - The asset
 */
async function getAsset(assetId: number, options?: ApiOptions): Promise<Asset> {
  const {data} = await apiV2Service.executeGetRequest<RemoteAsset>(
    `${BASE_PATH}/${assetId}`,
    {},
    options,
  );

  return convertAssetToClient(data);
}

/**
 * Get asset by URL
 * @param url - URL of the asset to retrieve
 * @param options - API options
 * @returns Promise<Asset> - The asset
 */
async function getAssetByUrl(url: string, options?: ApiOptions): Promise<Asset> {
  const {data} = await apiV2Service.executeGetRequest<RemoteAsset>(
    `${BASE_PATH}/by-url`,
    {u: url},
    options,
  );

  return convertAssetToClient(data);
}

/**
 * Generate zip URL for assets in a folder
 * @param entityType - Type of entity
 * @param folderId - ID of the folder
 * @param request - Request with asset IDs to include in zip
 * @param options - API options
 * @returns Promise<ZipUrlResponse> - URL to download the zip file
 */
async function generateFolderZipUrl(
  entityType: AssetFolderEntityType,
  folderId: number,
  request: GenerateZipRequest,
  options?: ApiOptions,
): Promise<number> {
  const {
    data: {executionId: jobId},
  } = await apiV2Service.executePostRequest<JobExecutionParams>(
    `${BASE_PATH}/${entityType}/folders/${folderId}/zipUrl`,
    {},
    request as Record<string, unknown>,
    options,
  );

  return jobId;
}

/**
 * Create a new folder
 * @param entityType - Type of entity
 * @param folderId - ID of the parent folder
 * @param request - Request with folder name
 * @param options - API options
 * @returns Promise<void>
 */
async function createFolder(
  entityType: AssetFolderEntityType,
  folderId: number,
  request: NewFolderRequest,
  options?: ApiOptions,
): Promise<void> {
  await apiV2Service.executePostRequest<void>(
    `${BASE_PATH}/${entityType}/folders/${folderId}/folder`,
    {},
    request as Record<string, unknown>,
    options,
  );
}

/**
 * Update a folder (rename)
 * @param entityType - Type of entity
 * @param folderId - ID of the folder to update
 * @param name - New folder name
 * @param options - API options
 * @returns Promise<void>
 */
async function updateFolder(
  entityType: AssetFolderEntityType,
  folderId: number,
  name: string,
  options?: ApiOptions,
): Promise<void> {
  await apiV2Service.executePatchRequest<void>(
    `${BASE_PATH}/${entityType}/folders/${folderId}`,
    {},
    name as unknown as Record<string, unknown>,
    options,
  );
}

/**
 * Delete a folder
 * @param entityType - Type of entity
 * @param folderId - ID of the folder to delete
 * @param options - API options
 * @returns Promise<void>
 */
async function deleteFolder(
  entityType: AssetFolderEntityType,
  folderId: number,
  options?: ApiOptions,
): Promise<void> {
  await apiV2Service.executeDeleteRequest<void>(
    `${BASE_PATH}/${entityType}/folders/${folderId}`,
    {},
    {},
    options,
  );
}

/**
 * Create a new asset in a folder
 * @param entityType - Type of entity
 * @param folderId - ID of the folder
 * @param request - Request with file URL
 * @param options - API options
 * @param jobName - Optional name for the background process (defaults to "Creating asset")
 * @returns Promise with jobId and bgProcess for monitoring the asset creation job
 */
async function createAssets(
  entityType: AssetFolderEntityType,
  folderId: number,
  request: NewAssetCreationgRequestForFolder,
  options?: ApiOptions,
): Promise<number> {
  const {
    data: {executionId: jobId},
  } = await apiV2Service.executePostRequest<JobExecutionParams>(
    `${BASE_PATH}/${entityType}/folders/${folderId}/assets`,
    {},
    request as Record<string, unknown>,
    options,
  );

  return jobId;
}

async function createFoldersAndAssets(
  entityType: AssetFolderEntityType,
  folderId: number,
  assetRequestItems: FolderAndAssetRequestItem[],
  emptyFoldersPath: string[] = [],
  options?: ApiOptions,
  skipBgProcess = false,
): Promise<{jobId: number; bgProcess: RunningBGProcess | null}> {
  const {
    data: {executionId: jobId},
  } = await apiV2Service.executePostRequest<JobExecutionParams>(
    `${BASE_PATH}/${entityType}/folders/${folderId}/foldersAndAssets`,
    {},
    {assets: assetRequestItems, emptyFoldersPath},
    options,
  );

  if (skipBgProcess) {
    return {jobId, bgProcess: null};
  }

  const bgProcess = backgroundProcessService.startProcess('Upload Assets', jobId.toString());
  bgProcess.setJobId(jobId);

  return {jobId, bgProcess};
}

/**
 * Delete an asset from a folder
 * @param entityType - Type of entity
 * @param folderId - ID of the folder
 * @param assetId - ID of the asset to delete
 * @param options - API options
 * @returns Promise<void>
 */
async function deleteAsset(
  entityType: AssetFolderEntityType,
  folderId: number,
  assetId: number,
  options?: ApiOptions,
): Promise<void> {
  await apiV2Service.executeDeleteRequest<void>(
    `${BASE_PATH}/${entityType}/folders/${folderId}/asset/${assetId}`,
    {},
    {},
    options,
  );
}

async function deleteMultipleAssetsAndFolders(
  entityType: AssetFolderEntityType,
  assetsUnderFolders: {folderId: number; assetId: number}[],
  folderIds: number[],
  options?: ApiOptions,
): Promise<void> {
  await apiV2Service.executePostRequest<void>(
    `${BASE_PATH}/${entityType}/foldersAndAssets/delete`,
    {},
    {folderIds: folderIds, assetsUnderFolders},
    options,
  );
}

/**
 * Rename an asset
 * @param assetId - ID of the asset to rename
 * @param newName - New name for the asset
 * @param options - API options
 * @returns Promise<void>
 */
async function renameAsset(assetId: number, newName: string, options?: ApiOptions): Promise<void> {
  await apiV2Service.executePatchRequest<void>(
    `${BASE_PATH}/${assetId}`,
    {},
    {name: newName},
    options,
  );
}

async function moveAssets(request: MoveAssetsRequest, options?: ApiOptions): Promise<void> {
  await apiV2Service.executePostRequest<void>(`${BASE_PATH}/move`, {}, {...request}, options);
}

async function copyPasteAssets(
  entityType: AssetFolderEntityType,
  request: CopyPasteAssetsRequest,
  options?: ApiOptions,
): Promise<void> {
  await apiV2Service.executePostRequest<void>(
    `${BASE_PATH}/${entityType}/copy-paste`,
    {},
    {...request},
    options,
  );
}

/**
 * Link an asset to a folder
 * @param assetId - ID of the asset to link
 * @param request - Request with target folder ID
 * @param options - API options
 * @returns Promise<void>
 */
async function linkAsset(
  assetId: number,
  request: LinkAssetRequest,
  options?: ApiOptions,
): Promise<void> {
  await apiV2Service.executePostRequest<void>(
    `${BASE_PATH}/${assetId}/link`,
    {},
    request as unknown as Record<string, unknown>,
    options,
  );
}

// Helper functions to convert Remote types to Client types
function convertTechnicalMetadataToClient(
  remoteTechnicalMetadata: RemoteTechnicalMetadata,
): TechnicalMetadata {
  return remoteTechnicalMetadata;
}

function convertAssetToClient(remoteAsset: RemoteAsset): Asset {
  return {
    ...remoteAsset,
    assetType: parseInt(remoteAsset.assetType, 10) as AssetType,
    originalfile: convertTechnicalMetadataToClient(remoteAsset.originalfile),
    derivedFiles: remoteAsset.derivedFiles?.map((file) => ({
      derivedFilePurpose: file.derivedFilePurpose,
      technicalMetadata: file.technicalMetadata
        ? convertTechnicalMetadataToClient(file.technicalMetadata)
        : null,
    })),
    createdTime: DateTime.fromISO(remoteAsset.createdTime),
    updatedTime: remoteAsset.updatedTime ? DateTime.fromISO(remoteAsset.updatedTime) : null,
    contentMetadata: {
      ...remoteAsset.contentMetadata,
      aiContentMetadata: {
        ...remoteAsset.contentMetadata.aiContentMetadata,
        stateUpdateTime: remoteAsset.contentMetadata.aiContentMetadata?.stateUpdateTime
          ? DateTime.fromISO(remoteAsset.contentMetadata.aiContentMetadata.stateUpdateTime)
          : null,
        metadataV1: remoteAsset.contentMetadata.aiContentMetadata?.metadataV1 ?? null,
      },
      detectedObjectsContentMetadata:
        remoteAsset.contentMetadata.detectedObjectsContentMetadata ?? null,
    },
  };
}

function convertFolderToClient(remoteFolder: RemoteFolder): Folder {
  return {
    folderId: remoteFolder.folderId,
    folderName: remoteFolder.folderName,
    folderPath: remoteFolder.folderPath,
    createdTime: DateTime.fromISO(remoteFolder.createdTime),
    createdBy: remoteFolder.createdBy,
    folders: remoteFolder.folders?.map(convertFolderToClient),
    assets: remoteFolder.assets?.map(convertAssetToClient),
    isChildrenLoaded: remoteFolder.isChildrenLoaded,
    isMatchSearchTerm: remoteFolder.isMatchSearchTerm,
    isReadonly: remoteFolder.isReadonly,
  };
}

const assetsApi = {
  getRootFolder,
  getRootFolderOfEntityObject,
  GetFolder,
  getAsset,
  getAssetByUrl,
  generateFolderZipUrl,
  createFolder,
  updateFolder,
  deleteFolder,
  createAssets,
  deleteAsset,
  renameAsset,
  moveAssets,
  linkAsset,
  createFoldersAndAssets,
  deleteMultipleAssetsAndFolders,
  copyPasteAssets,
};

export default assetsApi;
